import type { FriendResultMsg, ReportReason } from "@hupper/shared/protocol";

import type { GameClient, MatchHudState, RemotePlayerState } from "../net/GameClient";

/** Report reasons offered in the scoreboard popover (mapped to API enum). */
const REPORT_REASONS: ReadonlyArray<{ value: ReportReason; label: string }> = [
  { value: "cheating", label: "Cheating / Aimbot" },
  { value: "abuse", label: "Toxic / Abusive" },
  { value: "harassment", label: "Harassment" },
  { value: "scam", label: "Scam / Spam" },
  { value: "other", label: "Other" },
];

/** Triangle-with-exclamation glyph for the per-row report button. */
const WARN_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
  <path d="M12 3.4 L22 20 H2 Z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/>
  <line x1="12" y1="9.3" x2="12" y2="14.4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
  <circle cx="12" cy="17.2" r="1.2" fill="currentColor"/>
</svg>`;

/** Checkmark shown once a player has been reported this session. */
const CHECK_SVG = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
  <path d="M5 12.5 L10 17.5 L19 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

/** Add-friend glyph (person + plus) for the per-row friend button. */
const FRIEND_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
  <circle cx="9" cy="8" r="3.3" fill="none" stroke="currentColor" stroke-width="1.8"/>
  <path d="M3.4 19c0-3 2.5-5 5.6-5s5.6 2 5.6 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
  <line x1="19" y1="7.5" x2="19" y2="13.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
  <line x1="16" y1="10.5" x2="22" y2="10.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
</svg>`;

/** Person + check, shown once a friend request was sent this session. */
const FRIEND_SENT_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
  <circle cx="9" cy="8" r="3.3" fill="none" stroke="currentColor" stroke-width="1.8"/>
  <path d="M3.4 19c0-3 2.5-5 5.6-5s5.6 2 5.6 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
  <path d="M15 11 L17.6 13.6 L22 8.6" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

/**
 * TabScoreboard — hold `Tab` to see both players, CS:GO-style.
 *
 * Header shows the match score (CT vs T) + round. Each side is a table
 * with a colored team bar and rows of: ping, profile avatar (solid
 * color placeholder), name, kills, deaths, money. Money is only shown
 * for the local player's own team (CS:GO hides the enemy's economy).
 */
export class TabScoreboard {
  private readonly client: GameClient;
  private readonly root: HTMLDivElement;
  private readonly ctList: HTMLDivElement;
  private readonly tList: HTMLDivElement;
  private readonly ctTally: HTMLSpanElement;
  private readonly tTally: HTMLSpanElement;
  private readonly hdrCt: HTMLSpanElement;
  private readonly hdrT: HTMLSpanElement;
  private readonly hdrRound: HTMLDivElement;
  private readonly unsubs: Array<() => void> = [];
  private hud: MatchHudState | null = null;
  private visible = false;

  // ── Report popover state ──
  private readonly modal: HTMLDivElement;
  private readonly modalName: HTMLSpanElement;
  private readonly modalReasons: HTMLDivElement;
  private readonly modalConfirm: HTMLButtonElement;
  /** Players reported this session — disables the button + shows a check. */
  private readonly reported = new Set<string>();
  /** Players we've sent a friend request to this session — shows a check. */
  private readonly friendRequested = new Set<string>();
  /** Players the server confirmed we're friends with — shows a friends badge. */
  private readonly friends = new Set<string>();
  /** sessionId → display name, captured on click to label the ack toast. */
  private readonly friendNames = new Map<string, string>();
  /** True while the report dialog is open — keeps the board up past Tab-up. */
  private pinned = false;
  private reportTargetSid: string | null = null;
  private selectedReason: ReportReason | null = null;
  private toastTimer = 0;
  /** Mock roster for the `?reportdemo=1` preview; null in real matches. */
  private demoPlayers: RemotePlayerState[] | null = null;

  constructor(client: GameClient) {
    this.client = client;

    this.root = document.createElement("div");
    this.root.id = "tab-scoreboard";
    this.root.className = "hidden";
    this.root.innerHTML = `
      <div class="tsb-panel">
        <div class="tsb-header">
          <div class="tsb-match">
            <span class="tsb-hscore tsb-hscore-ct" data-hdr-ct>0</span>
            <span class="tsb-hsep">:</span>
            <span class="tsb-hscore tsb-hscore-t" data-hdr-t>0</span>
          </div>
          <div class="tsb-round" data-hdr-round>WARMUP</div>
        </div>

        <section class="tsb-side tsb-ct">
          <div class="tsb-bar">
            <span class="tsb-team">Counter-Terrorists</span>
            <span class="tsb-tally" data-ct-score>0</span>
          </div>
          <div class="tsb-cols">
            <span class="tsb-c-ping">Ping</span>
            <span class="tsb-c-name">Player</span>
            <span class="tsb-c-num">K</span>
            <span class="tsb-c-num">D</span>
            <span class="tsb-c-money">Money</span>
            <span class="tsb-c-report"></span>
          </div>
          <div class="tsb-rows" data-ct-rows></div>
        </section>

        <section class="tsb-side tsb-t">
          <div class="tsb-bar">
            <span class="tsb-team">Terrorists</span>
            <span class="tsb-tally" data-t-score>0</span>
          </div>
          <div class="tsb-cols">
            <span class="tsb-c-ping">Ping</span>
            <span class="tsb-c-name">Player</span>
            <span class="tsb-c-num">K</span>
            <span class="tsb-c-num">D</span>
            <span class="tsb-c-money">Money</span>
            <span class="tsb-c-report"></span>
          </div>
          <div class="tsb-rows" data-t-rows></div>
        </section>
      </div>`;
    this.ctList = this.root.querySelector("[data-ct-rows]") as HTMLDivElement;
    this.tList = this.root.querySelector("[data-t-rows]") as HTMLDivElement;
    this.ctTally = this.root.querySelector("[data-ct-score]") as HTMLSpanElement;
    this.tTally = this.root.querySelector("[data-t-score]") as HTMLSpanElement;
    this.hdrCt = this.root.querySelector("[data-hdr-ct]") as HTMLSpanElement;
    this.hdrT = this.root.querySelector("[data-hdr-t]") as HTMLSpanElement;
    this.hdrRound = this.root.querySelector("[data-hdr-round]") as HTMLDivElement;
    // Report popover — a centered card layered over the board. Built once and
    // toggled; the board's overlay already captures the cursor (see show()).
    this.modal = document.createElement("div");
    this.modal.className = "tsb-report-modal hidden";
    this.modal.innerHTML = `
      <div class="tsb-rm-card">
        <div class="tsb-rm-icon">${WARN_SVG}</div>
        <div class="tsb-rm-title">Report <span data-rm-name></span></div>
        <div class="tsb-rm-sub">Pick a reason — our admins review every report.</div>
        <div class="tsb-rm-reasons" data-rm-reasons>
          ${REPORT_REASONS.map(
            (r) => `<button class="tsb-rm-reason" data-reason="${r.value}">${r.label}</button>`,
          ).join("")}
        </div>
        <div class="tsb-rm-actions">
          <button class="tsb-rm-cancel" data-rm-cancel>Cancel</button>
          <button class="tsb-rm-confirm" data-rm-confirm disabled>Confirm report</button>
        </div>
      </div>`;
    this.root.appendChild(this.modal);
    this.modalName = this.modal.querySelector("[data-rm-name]") as HTMLSpanElement;
    this.modalReasons = this.modal.querySelector("[data-rm-reasons]") as HTMLDivElement;
    this.modalConfirm = this.modal.querySelector("[data-rm-confirm]") as HTMLButtonElement;
    this.modalReasons.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("[data-reason]") as HTMLElement | null;
      if (!btn) return;
      this.selectReason(btn.getAttribute("data-reason") as ReportReason);
    });
    this.modalConfirm.addEventListener("click", () => this.confirmReport());
    (this.modal.querySelector("[data-rm-cancel]") as HTMLButtonElement).addEventListener("click", () =>
      this.cancelReport(),
    );
    // Click on the dim backdrop (outside the card) cancels.
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.cancelReport();
    });

    // Row action buttons (friend + report) are wired by delegation on the
    // stable root (rows are replaced ~64Hz). `pointerdown` fires on press, so a
    // concurrent row rebuild can't destroy the node between mousedown and
    // mouseup and eat the click (the failure mode the buy menu hit in PR #244).
    this.root.addEventListener("pointerdown", (e) => {
      const el = e.target as HTMLElement;
      const friendBtn = el.closest(".tsb-friend") as HTMLElement | null;
      const reportBtn = el.closest(".tsb-report") as HTMLElement | null;
      const btn = friendBtn ?? reportBtn;
      if (!btn || btn.classList.contains("done")) return;
      e.preventDefault();
      const sid = btn.getAttribute("data-sid");
      if (!sid) return;
      const name = btn.getAttribute("data-name") || "anon";
      if (friendBtn) this.sendFriendReq(sid, name);
      else this.openReportPopover(sid, name);
    });

    document.body.appendChild(this.root);
    this.injectStyles();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape" && this.pinned) {
        e.preventDefault();
        this.cancelReport();
        return;
      }
      if (e.code !== "Tab" || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault();
      this.show();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== "Tab") return;
      e.preventDefault();
      this.hide();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    this.unsubs.push(() => window.removeEventListener("keydown", onKeyDown));
    this.unsubs.push(() => window.removeEventListener("keyup", onKeyUp));

    this.unsubs.push(this.client.onMatchHud((h) => {
      this.hud = h;
      if (this.visible) this.render();
    }));
    this.unsubs.push(this.client.onPlayerChange(() => { if (this.visible) this.render(); }));
    this.unsubs.push(this.client.onPlayerAdd(() => { if (this.visible) this.render(); }));
    this.unsubs.push(this.client.onPlayerRemove(() => { if (this.visible) this.render(); }));
    this.unsubs.push(this.client.onFriendResult((m) => this.onFriendResult(m)));

    // Pre-existing friendships (server push on join): seed the green badge so
    // a friend reads as "Friends" the moment the board opens, then track late
    // pushes (the opponent may join after this scoreboard is built).
    for (const sid of this.client.getFriendSids()) this.friends.add(sid);
    this.unsubs.push(this.client.onFriendStatus((m) => {
      if (!m.friends) return;
      this.friends.add(m.targetSessionId);
      if (this.visible) this.render();
    }));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    window.clearTimeout(this.toastTimer);
    document.getElementById("tab-scoreboard-toast")?.remove();
    this.root.remove();
  }

  private getCanvas(): HTMLCanvasElement | null {
    return document.getElementById("game-canvas") as HTMLCanvasElement | null;
  }

  /**
   * Standalone visual preview with mock players (no live match) — wired to
   * `?reportdemo=1` in main.ts, mirroring `?vsintro=1` / `?matchend=1`.
   */
  debugPreview(): void {
    const base = { x: 0, y: 0, z: 0, yaw: 0, pitch: 0, weaponId: "ak47", lastProcessedInputSeq: 0 };
    const mk = (over: Partial<RemotePlayerState>): RemotePlayerState =>
      ({ ...base, ...over }) as RemotePlayerState;
    const ct = [
      mk({ sessionId: "demo-self", name: "You", team: "ct", ping: 22, kills: 14, deaths: 6, money: 4200, health: 100 }),
      mk({ sessionId: "demo-mate", name: "Falcon", team: "ct", ping: 41, kills: 8, deaths: 10, money: 1500, health: 100 }),
    ];
    const t = [
      mk({ sessionId: "demo-e1", name: "Sn1per", team: "t", ping: 88, kills: 11, deaths: 9, money: 0, health: 0 }),
      mk({ sessionId: "demo-e2", name: "gh0st", team: "t", ping: 63, kills: 13, deaths: 7, money: 0, health: 100 }),
    ];
    this.demoPlayers = [...ct, ...t];
    this.friends.add("demo-mate"); // Falcon: already a friend → green "Friends" pill
    this.hud = { ctScore: 7, tScore: 5, phase: "live", roundNumber: 13 } as MatchHudState;
    this.visible = true;
    this.root.classList.remove("hidden");
    this.render();
  }

  private show(): void {
    this.visible = true;
    this.render();
    this.root.classList.remove("hidden");
    // Free the cursor so the report button is clickable. This also pauses
    // movement/firing (the controller gates both on pointer lock) — fine
    // while you're reading the scoreboard.
    try { document.exitPointerLock(); } catch { /* ignore */ }
  }

  private hide(): void {
    if (this.pinned) return; // a report dialog is open — keep the board up
    this.visible = false;
    this.root.classList.add("hidden");
    // Resume play. hide() is always reached from a user gesture (Tab keyup,
    // or a Confirm/Cancel click), so re-locking is permitted.
    try { this.getCanvas()?.requestPointerLock(); } catch { /* ignore */ }
  }

  /** Open the reason popover for a player and pin the board open. */
  private openReportPopover(sid: string, name: string): void {
    if (this.reported.has(sid)) return;
    this.reportTargetSid = sid;
    this.selectedReason = null;
    this.modalName.textContent = name;
    this.modalConfirm.disabled = true;
    this.modalReasons.querySelectorAll(".tsb-rm-reason.selected").forEach((b) =>
      b.classList.remove("selected"),
    );
    this.modal.classList.remove("hidden");
    this.pinned = true;
  }

  private selectReason(reason: ReportReason): void {
    this.selectedReason = reason;
    this.modalReasons.querySelectorAll(".tsb-rm-reason").forEach((b) =>
      b.classList.toggle("selected", b.getAttribute("data-reason") === reason),
    );
    this.modalConfirm.disabled = false;
  }

  private confirmReport(): void {
    const sid = this.reportTargetSid;
    const reason = this.selectedReason;
    if (!sid || !reason) return;
    this.client.sendReportPlayer(sid, reason);
    this.reported.add(sid);
    this.toast("Report submitted — thanks. Our team will review it.");
    this.closeModal();
    this.hide();
  }

  private cancelReport(): void {
    this.closeModal();
    this.hide();
  }

  /**
   * Fire a friend request and confirm it inline. The server auto-accepts when
   * the other player already requested us (mutual → friends) and is idempotent
   * if we're already friends; the real outcome comes back via onFriendResult,
   * which corrects this optimistic "sent" message. No dialog — one click sends
   * it, like the buy buttons.
   */
  private sendFriendReq(sid: string, name: string): void {
    this.friendNames.set(sid, name);
    if (this.friends.has(sid)) {
      this.toast(`You're already friends with ${name}.`);
      return;
    }
    if (this.friendRequested.has(sid)) return; // already pending this session
    this.client.sendFriendRequest(sid);
    this.friendRequested.add(sid);
    this.toast(`Friend request sent to ${name}.`);
    if (this.visible) this.render(); // swap the icon to its "sent" check
  }

  /** Server's verdict on a friend request — upgrades the optimistic UI. */
  private onFriendResult(msg: FriendResultMsg): void {
    const sid = msg.targetSessionId;
    const name = this.friendNames.get(sid) || "this player";
    if (msg.result === "accepted" || msg.result === "already_friends") {
      this.friends.add(sid);
      this.friendRequested.delete(sid);
      this.toast(
        msg.result === "accepted"
          ? `You're now friends with ${name}!`
          : `You're already friends with ${name}.`,
      );
    } else {
      // requested / already_pending — keep the "sent" state (already shown).
      this.friendRequested.add(sid);
    }
    if (this.visible) this.render();
  }

  /** Hide the popover and unpin, without touching board visibility. */
  private closeModal(): void {
    this.modal.classList.add("hidden");
    this.pinned = false;
    this.reportTargetSid = null;
    this.selectedReason = null;
    if (this.visible) this.render(); // refresh the row's reported state
  }

  /** Brief confirmation toast, anchored to the screen (survives board hide). */
  private toast(text: string): void {
    let el = document.getElementById("tab-scoreboard-toast");
    if (!el) {
      el = document.createElement("div");
      el.id = "tab-scoreboard-toast";
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.classList.remove("show");
    // Force reflow so re-triggering the fade restarts the animation.
    void el.offsetWidth;
    el.classList.add("show");
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => el?.classList.remove("show"), 2600);
  }

  private render(): void {
    const ctScore = this.hud?.ctScore ?? 0;
    const tScore = this.hud?.tScore ?? 0;
    this.ctTally.textContent = String(ctScore);
    this.tTally.textContent = String(tScore);
    this.hdrCt.textContent = String(ctScore);
    this.hdrT.textContent = String(tScore);
    const phase = this.hud?.phase ?? "";
    const roundNo = this.hud?.roundNumber ?? 0;
    this.hdrRound.textContent =
      phase === "warmup" ? "WARMUP" : roundNo > 0 ? `ROUND ${roundNo}` : phase.toUpperCase();

    // Only the local player's team sees money (CS:GO hides enemy economy).
    const localTeam = this.client.getLatestSelf()?.team ?? "ct";

    const ct: RemotePlayerState[] = [];
    const t: RemotePlayerState[] = [];
    const collect = (p: RemotePlayerState) => {
      (p.team === "t" ? t : ct).push(p);
    };
    // `demoPlayers` backs the `?reportdemo=1` preview (no live match).
    if (this.demoPlayers) this.demoPlayers.forEach(collect);
    else this.client.forEachKnownPlayer(collect);

    this.ctList.replaceChildren(...ct.map((p) => this.row(p, localTeam)));
    this.tList.replaceChildren(...t.map((p) => this.row(p, localTeam)));
  }

  private row(p: RemotePlayerState, localTeam: "ct" | "t"): HTMLDivElement {
    const isSelf = p.sessionId === this.client.sessionId;
    const dead = (p.health ?? 0) <= 0;
    const ping = Math.max(0, Math.round(p.ping ?? 0));
    const kills = Math.max(0, Math.round(p.kills ?? 0));
    const deaths = Math.max(0, Math.round(p.deaths ?? 0));
    const sameTeam = (p.team ?? "ct") === localTeam;
    const money = p.money ?? 0;

    const row = document.createElement("div");
    row.className = `tsb-row${isSelf ? " self" : ""}${dead ? " dead" : ""}`;

    const pingClass = ping < 60 ? "good" : ping < 120 ? "ok" : "bad";
    // Money is only visible for your own team (CS:GO hides enemy economy
    // entirely — the opponent's cell is blank).
    const moneyCell = sameTeam
      ? `<span class="tsb-money">$${money.toLocaleString("en-US")}</span>`
      : `<span class="tsb-money tsb-hidden"></span>`;

    // Avatar: the player's uploaded photo when we have one, otherwise a
    // deterministic colored tile with their initial. The <img> sits on top
    // of the colored tile and is removed on load error, so a 404 falls
    // straight back to the generated avatar (mirrors the web app's Avatar).
    const hasPhoto = !!p.avatarUrl;
    const avatarInner = hasPhoto
      ? `<span class="tsb-avatar-initial">${initial(p.name)}</span>` +
        `<img class="tsb-avatar-img" src="${escapeHtml(p.avatarUrl!)}" alt="">`
      : initial(p.name);

    // Friend + report buttons — every player except yourself. Each swaps to a
    // check once used this session.
    const sid = escapeHtml(p.sessionId);
    const nm = escapeHtml(p.name || "anon");
    const reported = this.reported.has(p.sessionId);
    const isFriend = this.friends.has(p.sessionId);
    const requested = this.friendRequested.has(p.sessionId);
    // Three states: confirmed friend (green, locked), request pending (green
    // check, locked), or add-friend (cyan, clickable).
    const friendUsed = isFriend || requested;
    const friendTitle = isFriend ? "Friends" : requested ? "Friend request sent" : "Add friend";
    const friendBtn =
      `<button class="tsb-friend${isFriend ? " friends" : ""}${friendUsed ? " done" : ""}"` +
      ` ${friendUsed ? "disabled" : ""} data-sid="${sid}" data-name="${nm}"` +
      ` title="${friendTitle}" aria-label="${friendTitle}">` +
      `${friendUsed ? FRIEND_SENT_SVG : FRIEND_SVG}</button>`;
    const reportBtn =
      `<button class="tsb-report${reported ? " done" : ""}" ${reported ? "disabled" : ""}` +
      ` data-sid="${sid}" data-name="${nm}"` +
      ` title="${reported ? "Reported" : "Report player"}" aria-label="Report player">` +
      `${reported ? CHECK_SVG : WARN_SVG}</button>`;
    const reportCell = isSelf
      ? `<span class="tsb-actions"></span>`
      : `<span class="tsb-actions">${friendBtn}${reportBtn}</span>`;

    row.innerHTML = `
      <span class="tsb-ping ${pingClass}"><i></i>${ping}</span>
      <span class="tsb-player">
        <span class="tsb-avatar" style="background:${avatarColor(p.name || p.sessionId)}">${avatarInner}</span>
        <span class="tsb-name">${escapeHtml(p.name || "anon")}</span>
        ${isSelf ? '<span class="tsb-you">YOU</span>' : ""}
      </span>
      <span class="tsb-num">${kills}</span>
      <span class="tsb-num tsb-deaths">${deaths}</span>
      ${moneyCell}
      ${reportCell}
    `;
    // Reveal the colored-initial fallback if the photo fails to load.
    const img = row.querySelector(".tsb-avatar-img") as HTMLImageElement | null;
    if (img) img.addEventListener("error", () => img.remove());
    // NB: the friend + report buttons are wired via delegation in the
    // constructor, not per-row listeners — rows are rebuilt ~64Hz from snapshot
    // churn, which would destroy a pressed button mid-click (buy-menu PR #244).
    return row;
  }

  private injectStyles(): void {
    if (document.getElementById("tab-scoreboard-styles")) return;
    const style = document.createElement("style");
    style.id = "tab-scoreboard-styles";
    style.textContent = `
      #tab-scoreboard {
        position: fixed; inset: 0; z-index: 30;
        display: flex; align-items: center; justify-content: center;
        background: radial-gradient(circle at 50% 40%, rgba(10,12,16,0.55), rgba(6,7,10,0.82));
        backdrop-filter: blur(4px);
        /* Interactive: the cursor is freed on show() so the report buttons
           are clickable; this overlay also blocks clicks from reaching the
           game canvas behind it. */
        pointer-events: auto;
        font-family: "Inter", "Segoe UI", ui-sans-serif, system-ui, sans-serif;
      }
      #tab-scoreboard.hidden { display: none; }

      #tab-scoreboard .tsb-panel {
        width: min(960px, 96vw);
        background: linear-gradient(180deg, rgba(22,25,32,0.97), rgba(14,16,21,0.97));
        border: 1px solid rgba(255,255,255,0.08);
        border-radius: 4px;
        overflow: hidden;
        box-shadow: 0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04);
      }

      /* ── Match header ── */
      #tab-scoreboard .tsb-header {
        display: flex; flex-direction: column; align-items: center;
        gap: 2px; padding: 16px 0 12px;
        background: linear-gradient(180deg, rgba(0,0,0,0.25), transparent);
        border-bottom: 1px solid rgba(255,255,255,0.06);
      }
      #tab-scoreboard .tsb-match {
        display: flex; align-items: baseline; gap: 16px;
        font-variant-numeric: tabular-nums;
      }
      #tab-scoreboard .tsb-hscore { font-size: 44px; font-weight: 900; line-height: 1; }
      #tab-scoreboard .tsb-hscore-ct { color: #6f9fe0; }
      #tab-scoreboard .tsb-hscore-t  { color: #e0b14a; }
      #tab-scoreboard .tsb-hsep { font-size: 30px; font-weight: 700; color: #4a4a52; }
      #tab-scoreboard .tsb-round {
        font-size: 11px; letter-spacing: 0.32em; text-transform: uppercase;
        color: #7a7a84; font-weight: 700;
      }

      /* ── Team section ── */
      #tab-scoreboard .tsb-side { padding: 0 0 6px; }
      #tab-scoreboard .tsb-bar {
        display: flex; align-items: center; justify-content: space-between;
        padding: 8px 18px;
      }
      #tab-scoreboard .tsb-ct .tsb-bar { background: linear-gradient(90deg, rgba(79,140,224,0.20), transparent); border-left: 3px solid #4f8ce0; }
      #tab-scoreboard .tsb-t  .tsb-bar { background: linear-gradient(90deg, rgba(224,177,74,0.20), transparent); border-left: 3px solid #e0b14a; }
      #tab-scoreboard .tsb-team {
        font-size: 14px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
      }
      #tab-scoreboard .tsb-ct .tsb-team { color: #8fbcf2; }
      #tab-scoreboard .tsb-t  .tsb-team { color: #f0c869; }
      #tab-scoreboard .tsb-tally { font-size: 20px; font-weight: 900; color: #fff; font-variant-numeric: tabular-nums; }

      /* ── Columns / rows grid ── */
      #tab-scoreboard .tsb-cols,
      #tab-scoreboard .tsb-row {
        display: grid;
        grid-template-columns: 64px 1fr 40px 40px 104px 68px;
        gap: 6px; align-items: center;
      }
      #tab-scoreboard .tsb-c-report { display: block; }
      #tab-scoreboard .tsb-cols {
        padding: 7px 18px 5px;
        font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: #5a5a63;
      }
      #tab-scoreboard .tsb-c-num { text-align: center; }
      #tab-scoreboard .tsb-c-money { text-align: right; }

      #tab-scoreboard .tsb-rows { padding: 0 10px; }
      #tab-scoreboard .tsb-row {
        padding: 8px 8px; border-radius: 3px; border-left: 3px solid transparent;
      }
      #tab-scoreboard .tsb-row:nth-child(odd) { background: rgba(255,255,255,0.025); }
      #tab-scoreboard .tsb-row.self {
        background: rgba(255,255,255,0.08);
        border-left-color: rgba(255,255,255,0.5);
      }
      #tab-scoreboard .tsb-row.dead { opacity: 0.4; }

      /* ping */
      #tab-scoreboard .tsb-ping {
        display: inline-flex; align-items: center; gap: 5px;
        font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; color: #c8c8cf;
      }
      #tab-scoreboard .tsb-ping i { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
      #tab-scoreboard .tsb-ping.good i { background: #5cd685; box-shadow: 0 0 6px rgba(92,214,133,0.7); }
      #tab-scoreboard .tsb-ping.ok i   { background: #f0c44a; }
      #tab-scoreboard .tsb-ping.bad i  { background: #ff5d5d; }

      /* player */
      #tab-scoreboard .tsb-player { display: flex; align-items: center; gap: 11px; min-width: 0; }
      #tab-scoreboard .tsb-avatar {
        position: relative; overflow: hidden;
        width: 30px; height: 30px; border-radius: 4px; flex: none;
        display: flex; align-items: center; justify-content: center;
        font-size: 14px; font-weight: 900; color: rgba(0,0,0,0.55);
        box-shadow: inset 0 0 0 1px rgba(255,255,255,0.18), 0 1px 3px rgba(0,0,0,0.4);
      }
      /* Uploaded photo sits on top of the colored initial; if it 404s the
         <img> is removed (see row()) and the initial shows through. */
      #tab-scoreboard .tsb-avatar-img {
        position: absolute; inset: 0; width: 100%; height: 100%;
        object-fit: cover; display: block;
      }
      #tab-scoreboard .tsb-name {
        font-size: 16px; font-weight: 700; color: #f4f4ef;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      #tab-scoreboard .tsb-you {
        font-size: 9px; font-weight: 800; letter-spacing: 0.1em;
        color: #11131a; background: #d9d9d1; border-radius: 2px; padding: 2px 5px;
      }

      /* numbers + money */
      #tab-scoreboard .tsb-num {
        text-align: center; font-size: 16px; font-weight: 800;
        font-variant-numeric: tabular-nums; color: #f4f4ef;
      }
      #tab-scoreboard .tsb-deaths { color: #9a9aa2; }
      #tab-scoreboard .tsb-money {
        text-align: right; font-size: 15px; font-weight: 800; color: #5cd685;
        font-variant-numeric: tabular-nums;
      }
      #tab-scoreboard .tsb-money.tsb-hidden { color: #3a3a42; font-weight: 600; }

      /* ── Row action buttons (friend + report) ── */
      #tab-scoreboard .tsb-actions {
        display: flex; align-items: center; justify-content: flex-end; gap: 4px;
      }
      #tab-scoreboard .tsb-friend,
      #tab-scoreboard .tsb-report {
        width: 28px; height: 28px; padding: 0; flex: none;
        display: inline-flex; align-items: center; justify-content: center;
        border: 1px solid transparent; border-radius: 6px;
        background: transparent; cursor: pointer;
        transition: color .12s, background .12s, border-color .12s, transform .12s;
      }
      #tab-scoreboard .tsb-friend svg,
      #tab-scoreboard .tsb-report svg { display: block; }
      /* Friend — cyan. */
      #tab-scoreboard .tsb-friend { color: rgba(110,200,224,0.55); }
      #tab-scoreboard .tsb-friend:hover {
        color: #7fdcf2; background: rgba(110,200,224,0.14);
        border-color: rgba(110,200,224,0.45); transform: translateY(-1px);
      }
      /* Report — amber. */
      #tab-scoreboard .tsb-report { color: rgba(240,180,74,0.5); }
      #tab-scoreboard .tsb-report:hover {
        color: #ffce5a; background: rgba(240,180,74,0.14);
        border-color: rgba(240,180,74,0.45); transform: translateY(-1px);
      }
      /* Both go green once used, and stop reacting to hover. */
      #tab-scoreboard .tsb-friend.done,
      #tab-scoreboard .tsb-report.done { color: #5cd685; cursor: default; }
      #tab-scoreboard .tsb-friend.done:hover,
      #tab-scoreboard .tsb-report.done:hover {
        background: transparent; border-color: transparent; transform: none;
      }
      /* Confirmed friend — a filled green pill so it reads differently from a
         still-pending request. */
      #tab-scoreboard .tsb-friend.friends,
      #tab-scoreboard .tsb-friend.friends:hover {
        background: rgba(92,214,133,0.15); border-color: rgba(92,214,133,0.45);
      }

      /* ── Report reason popover ── */
      #tab-scoreboard .tsb-report-modal {
        position: absolute; inset: 0; z-index: 5;
        display: flex; align-items: center; justify-content: center;
        background: rgba(6,7,10,0.66); backdrop-filter: blur(2px);
      }
      #tab-scoreboard .tsb-report-modal.hidden { display: none; }
      #tab-scoreboard .tsb-rm-card {
        width: min(380px, 90%);
        background: linear-gradient(180deg, rgba(28,30,38,0.99), rgba(18,20,26,0.99));
        border: 1px solid rgba(240,180,74,0.28); border-radius: 10px;
        padding: 22px 22px 18px; text-align: center;
        box-shadow: 0 24px 70px rgba(0,0,0,0.65);
      }
      #tab-scoreboard .tsb-rm-icon { color: #ffce5a; display: flex; justify-content: center; margin-bottom: 6px; }
      #tab-scoreboard .tsb-rm-icon svg { width: 38px; height: 38px; }
      #tab-scoreboard .tsb-rm-title { font-size: 18px; font-weight: 800; color: #f4f4ef; }
      #tab-scoreboard .tsb-rm-title span { color: #ffce5a; }
      #tab-scoreboard .tsb-rm-sub { font-size: 12px; color: #9a9aa2; margin: 6px 0 16px; }
      #tab-scoreboard .tsb-rm-reasons { display: flex; flex-direction: column; gap: 7px; margin-bottom: 16px; }
      #tab-scoreboard .tsb-rm-reason {
        appearance: none; text-align: left;
        padding: 10px 13px; border-radius: 7px;
        border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.03);
        color: #e6e6e1; font-size: 13.5px; font-weight: 600; cursor: pointer;
        transition: border-color .1s, background .1s, color .1s;
      }
      #tab-scoreboard .tsb-rm-reason:hover { border-color: rgba(255,255,255,0.28); background: rgba(255,255,255,0.06); }
      #tab-scoreboard .tsb-rm-reason.selected { border-color: #ffce5a; background: rgba(240,180,74,0.16); color: #ffe7a8; }
      #tab-scoreboard .tsb-rm-actions { display: flex; gap: 10px; }
      #tab-scoreboard .tsb-rm-actions button {
        flex: 1; padding: 10px 0; border-radius: 7px; font-size: 13px; font-weight: 800;
        cursor: pointer; border: 1px solid transparent;
        transition: opacity .1s, background .1s, border-color .1s;
      }
      #tab-scoreboard .tsb-rm-cancel { background: rgba(255,255,255,0.06); color: #cfcfd6; border-color: rgba(255,255,255,0.12); }
      #tab-scoreboard .tsb-rm-cancel:hover { background: rgba(255,255,255,0.1); }
      #tab-scoreboard .tsb-rm-confirm { background: #e0563a; color: #fff; }
      #tab-scoreboard .tsb-rm-confirm:hover { background: #f0654a; }
      #tab-scoreboard .tsb-rm-confirm:disabled { opacity: 0.4; cursor: not-allowed; }
    `;
    document.head.appendChild(style);

    // Confirmation toast lives on <body> so it outlives the board hiding.
    const toast = document.createElement("style");
    toast.id = "tab-scoreboard-toast-styles";
    toast.textContent = `
      #tab-scoreboard-toast {
        position: fixed; left: 50%; bottom: 56px; transform: translate(-50%, 8px);
        z-index: 40; pointer-events: none; opacity: 0;
        background: rgba(20,22,28,0.96); border: 1px solid rgba(92,214,133,0.4);
        color: #d7f7e2; font-size: 13px; font-weight: 700;
        padding: 10px 16px; border-radius: 8px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.5);
        font-family: "Inter", "Segoe UI", ui-sans-serif, system-ui, sans-serif;
        transition: opacity .18s ease, transform .18s ease;
      }
      #tab-scoreboard-toast.show { opacity: 1; transform: translate(-50%, 0); }
    `;
    document.head.appendChild(toast);
  }
}

/** Deterministic solid color from a string — placeholder profile avatar. */
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 55%, 55%)`;
}

/** First letter for the avatar glyph. */
function initial(name: string | undefined): string {
  const c = (name || "?").trim().charAt(0).toUpperCase();
  return escapeHtml(c || "?");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
